import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Alert, Box, Breadcrumbs, Button, CircularProgress,
  FormControl, Grid2, Snackbar, TextField, Typography,
} from '@mui/material';
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import AppShell from './components/AppShell';
import QuotaSection from './logic/QuotasSection';
import api from './services/api';

const ManualQuotas = () => {
  const { surveyId } = useParams();
  const [survey, setSurvey] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [overallN, setOverallN] = useState(1500);
  const [sectionState, setSectionState] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState(null);

  useEffect(() => {
    api.get(`/surveys/${surveyId}/`).then((res) => {
      setSurvey(res.data);
      api.get('/alchemer/questions/', { params: { survey_id: res.data.alchemer_survey_id } }).then((qRes) => {
        setQuestions(qRes.data.data.map((q) => ({ id: q.id, title: q.title, options: q.options || [] })));
      });
    });
  }, [surveyId]);

  const handleSubmit = async () => {
    const { quotaRows, logicTemplate, description } = sectionState;
    if (!quotaRows || quotaRows.length === 0) {
      setSnackbar({ severity: 'warning', message: 'Add at least one quota row before submitting.' });
      return;
    }

    const sections = [{ name: 'Manual', logicTemplate, quotaRows, description }];

    setSubmitting(true);
    try {
      const res = await api.post(`/surveys/${surveyId}/create-nat-rep-quotas/`, { overallN, sections });
      const { created, errors } = res.data;
      if (errors.length) {
        setSnackbar({ severity: 'warning', message: `Created ${created.length} quota(s). ${errors.length} error(s): ${errors[0]}` });
      } else {
        setSnackbar({ severity: 'success', message: `Successfully created ${created.length} quota(s) in Alchemer.` });
      }
    } catch (err) {
      setSnackbar({ severity: 'error', message: `Failed to submit quotas: ${err.response?.data?.error || err.message}` });
    } finally {
      setSubmitting(false);
    }
  };

  if (!survey) return null;

  return (
    <AppShell maxWidth="md">
      <Breadcrumbs separator=">" sx={{ mb: 4 }}>
        <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>Home</Link>
        <Link to={`/project/${survey.project}`} style={{ textDecoration: 'none', color: 'inherit' }}>{survey.project_name}</Link>
        <Link to={`/survey/${surveyId}`} style={{ textDecoration: 'none', color: 'inherit' }}>{survey.name}</Link>
        <Link to={`/survey/${surveyId}/add-quotas`} style={{ textDecoration: 'none', color: 'inherit' }}>Alchemer quota setup</Link>
        <Typography>Manual Quotas</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 4 }}>
        <EditNoteRoundedIcon color="primary" />
        <Typography variant="h4" fontFamily="'Work Sans', sans-serif" fontWeight={700}>
          Manual Quotas
        </Typography>
      </Box>

      <Box
        sx={{
          bgcolor: 'white',
          border: '1px solid #e0e0e0',
          borderRadius: 3,
          p: 3,
          mb: 3,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">Total sample size (N)</Typography>
        <FormControl size="small">
          <TextField
            type="number"
            size="small"
            value={overallN}
            onChange={(e) => setOverallN(Number(e.target.value) || 0)}
            sx={{ width: 140 }}
          />
        </FormControl>
      </Box>

      <QuotaSection
        title="Quota cells"
        initialRows={[]}
        value={sectionState}
        onChange={setSectionState}
        questions={questions}
        overallN={overallN}
        allowAddRows
        showDescription
      />

      <Grid2 container justifyContent="flex-end" sx={{ mt: 3 }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
          sx={{ textTransform: 'none', borderRadius: 2 }}
        >
          {submitting ? 'Submitting…' : 'Submit quotas'}
        </Button>
      </Grid2>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={6000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar(null)} severity={snackbar?.severity} sx={{ width: '100%' }}>
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </AppShell>
  );
};

export default ManualQuotas;
